// @flow

import { createSelector } from "reselect";

import {
  type MedidaSelecionada,
  type EdicaoMedida,
  type Id,
  ID_PERIODO_DATA_ESPECIFICA_INDICADORES_MERCADO,
  ID_PERIODO_ANO_A_ANO,
  ID_PERIODO_DATAS_REFERENCIA,
  ID_PERIODO_ANALISE_HORIZONTAL,
  INICIO_FAIXA_IDS_MESES_FECHADOS,
  FINAL_FAIXA_IDS_MESES_FECHADOS,
  ID_PERIODO_NOS_ULTIMOS_4_MESES,
  ID_PERIODO_ULTIMA_DIVULGACAO,
  ID_PERIODO_ULTIMA_100_PORCENTO_ABERTA,
  ID_PERIODO_NO_DIA_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_5_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_10_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_15_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_30_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_60_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_90_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_120_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_180_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MENOS_360_DIAS_INDICADORES_MERCADO,
  ID_PERIODO_MES_A_MES,
  ID_PERIODO_MES_A_MES_MENSAL,
  ID_PERIODO_DIA_A_DIA,
  ID_PERIODO_JANELA,
  ID_PERIODO_DATAS_ESPECIFICAS,
  ID_PERIODO_DATAS_ESPECIFICAS_NAO_MENSAL,
  PERIODOS_ESTENDIDOS,
  FORMATACAO_MOEDAS,
  FORMATACAO_MOEDA_MILHARES,
  MODO_APP_SERIES,
  MEDIDAS_SEM_CASAS_DECIMAIS,
} from "../../model";
import {
  type State,
  type GeradorMedidasSelecionadas,
  comporGeradorDeMedidas,
  getEdicaoMedida,
  getModoApp,
  getPeriodosSelecionados,
  getUltimos4MesesCompostos,
} from "../../state";
import { randomId } from "../../util";
import {
  processarPeriodoDiaADia,
  processarPeriodoDataEspecifica,
  processarPeriodoMesAMes,
  processarPeriodoJanela,
  processarPeriodoDatasEspecificas,
  processarPeriodoDatasIndicadoresMercado,
} from "../../components/edicaoMedidas/configuracoes/periodos/periodosEstendidos";
import {
  processarPeriodoAnoAAno,
  processarPeriodoDatasIndicadoresFinanceiros,
  processarPeriodoAnaliseHorizontal,
} from "../../components/edicaoMedidas/configuracoes/";
import { getPeriodoPorId } from "./dominioSelectors";

export type ProcessadorPeriodo = (
  Id,
  State,
  Array<MedidaSelecionada>
) => Array<MedidaSelecionada>;

const processarPeriodoSimples: ProcessadorPeriodo = (
  periodo: Id,
  state: State,
  medidasSelcionadas: Array<MedidaSelecionada>
) => {
  const _medidasSelcionadas = [];

  medidasSelcionadas.forEach(medidaSelecionada => {
    _medidasSelcionadas.push({ ...medidaSelecionada, periodo });
  });

  return _medidasSelcionadas;
};

const processarPeriodoMesFechado: ProcessadorPeriodo = (
  periodo: Id,
  state: State,
  medidasSelcionadas: Array<MedidaSelecionada>
) => {
  const _medidasSelcionadas = [];
  const chaveCalculo: string = getPeriodoPorId(state)(periodo).chaveCalculo;

  medidasSelcionadas.forEach(medidaSelecionada => {
    _medidasSelcionadas.push({
      ...medidaSelecionada,
      periodo,
      dataInicialComposta: chaveCalculo,
    });
  });

  return _medidasSelcionadas;
};

const processarPeriodoNosUltimos4Meses: ProcessadorPeriodo = (
  periodo: Id,
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
) => {
  const ultimos4MesesCompostos = getUltimos4MesesCompostos(state).reverse();

  const medidasProcessadas = ultimos4MesesCompostos.flatMap((mes, index) => {
    const idGrupo = randomId();
    return medidasSelecionadas.map(medidaSelecionada => ({
      ...medidaSelecionada,
      idGrupoMedidasSelecionadas: idGrupo,
      periodo,
      dataInicialComposta: mes.nome,
      indiceUltimos4Meses: index,
    }));
  });

  return medidasProcessadas;
};

const obterProcessadorPeriodos: Id => ProcessadorPeriodo = (periodo: Id) => {
  if (
    periodo >= INICIO_FAIXA_IDS_MESES_FECHADOS &&
    periodo <= FINAL_FAIXA_IDS_MESES_FECHADOS
  ) {
    return processarPeriodoMesFechado;
  }

  switch (periodo) {
    case ID_PERIODO_DATAS_REFERENCIA:
      return processarPeriodoDatasIndicadoresFinanceiros;
    case ID_PERIODO_ANALISE_HORIZONTAL:
      return processarPeriodoAnaliseHorizontal;
    case ID_PERIODO_DATA_ESPECIFICA_INDICADORES_MERCADO:
      return processarPeriodoDataEspecifica;
    case ID_PERIODO_MES_A_MES:
    case ID_PERIODO_MES_A_MES_MENSAL:
      return processarPeriodoMesAMes;
    case ID_PERIODO_ANO_A_ANO:
      return processarPeriodoAnoAAno;
    case ID_PERIODO_DIA_A_DIA:
      return processarPeriodoDiaADia;
    case ID_PERIODO_JANELA:
      return processarPeriodoJanela;
    case ID_PERIODO_DATAS_ESPECIFICAS:
    case ID_PERIODO_DATAS_ESPECIFICAS_NAO_MENSAL:
      return processarPeriodoDatasEspecificas;
    case ID_PERIODO_NO_DIA_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_5_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_10_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_15_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_30_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_60_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_90_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_120_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_180_DIAS_INDICADORES_MERCADO:
    case ID_PERIODO_MENOS_360_DIAS_INDICADORES_MERCADO:
      return processarPeriodoDatasIndicadoresMercado;
    case ID_PERIODO_NOS_ULTIMOS_4_MESES:
      return processarPeriodoNosUltimos4Meses;
    default:
      return processarPeriodoSimples;
  }
};

export const gerarMedidasSelecionadasPorPeriodo: GeradorMedidasSelecionadas = (
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
) => {
  const edicaoMedida: EdicaoMedida = getEdicaoMedida(state);

  switch (getModoApp(state)) {
    // Modo séries a app não gera uma medida por período
    case "SERIES":
      return medidasSelecionadas;

    default:
      if (edicaoMedida.periodos && edicaoMedida.periodos.length) {
        return edicaoMedida.periodos.reduce(
          (processadas, periodo) => [
            ...processadas,
            ...obterProcessadorPeriodos(periodo)(
              periodo,
              state,
              medidasSelecionadas
            ),
          ],
          []
        );
      } else {
        return medidasSelecionadas;
      }
  }
};

export const comGeradorDePeriodo = (
  gerador: GeradorMedidasSelecionadas
): GeradorMedidasSelecionadas =>
  comporGeradorDeMedidas(gerarMedidasSelecionadasPorPeriodo, gerador);

export const getIdsPeriodosEstendidos = (state: State): Array<Id> =>
  PERIODOS_ESTENDIDOS;

export function getPeriodosEstendidosSelecionados(state: State): ?Array<Id> {
  const periodosSelecionados: ?Array<Id> = getPeriodosSelecionados(state);
  const idsPeriodosEstendidos: Array<Id> = getIdsPeriodosEstendidos(state);
  return (
    periodosSelecionados &&
    periodosSelecionados.filter(periodo =>
      idsPeriodosEstendidos.includes(periodo)
    )
  );
}

export const getPeriodosIndividuais: State => Array<Id> = createSelector(
  getIdsPeriodosEstendidos,
  estendidos => [
    ...estendidos,
    ID_PERIODO_NOS_ULTIMOS_4_MESES,
    ID_PERIODO_ULTIMA_DIVULGACAO,
    ID_PERIODO_ULTIMA_100_PORCENTO_ABERTA,
  ]
);

export const getPeriodosIndividuaisSelecionados: State => ?Array<Id> = createSelector(
  getPeriodosSelecionados,
  getPeriodosEstendidosSelecionados,
  (selecionados, estendidos) => {
    if (selecionados) {
      return [
        ...estendidos,
        ...selecionados.filter(
          periodo => periodo === ID_PERIODO_NOS_ULTIMOS_4_MESES
        ),
      ];
    }
  }
);

// TODO: Adicionar tipagem do retorno corretamente
export const analiseValorDaMedida = (
  medidaSelecionada: MedidaSelecionada,
  state: State
): any => {
  const edicaoMedida = getEdicaoMedida(state);
  const inserirEscala = () => {
    if (
      medidaSelecionada &&
      medidaSelecionada.medida.formatacao &&
      (FORMATACAO_MOEDAS.includes(medidaSelecionada.medida.formatacao) ||
        medidaSelecionada.medida.formatacao === FORMATACAO_MOEDA_MILHARES)
    ) {
      return edicaoMedida.escala;
    }
  };
  const inserirCasasDecimais = () => {
    if (
      medidaSelecionada &&
      medidaSelecionada.medida.formatacao &&
      !MEDIDAS_SEM_CASAS_DECIMAIS.includes(medidaSelecionada.medida.formatacao)
    ) {
      return edicaoMedida.casasDecimais;
    }
  };

  if (getModoApp(state) === MODO_APP_SERIES) {
    return {
      escala: inserirEscala() ?? null,
      casasDecimais: inserirCasasDecimais() ?? null,
    };
  } else {
    return {
      casasDecimais: inserirCasasDecimais() ?? null,
    };
  }
};
